import { useCallback, useMemo, useState, type ChangeEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { UpdatedFact } from '@/components/product-insights/fab-ui';
import {
  PiDeniedPage,
  PiError,
  PiFilterChips,
  PiPageLayout,
  PiTabs,
  PiToolbar,
} from '@/components/product-insights/pi-kit';
import { useFabFilters } from '@/components/product-insights/use-fab-data';
import {
  SchoolProfilesEngagement,
  SchoolProfilesStats,
  SchoolProfilesViewsByChannel,
  SchoolProfilesViewsByCountry,
} from '@/components/product-insights/school-profiles/SchoolProfilesOverview';
import { SchoolProfilesTable } from '@/components/product-insights/school-profiles/SchoolProfilesTable';
import {
  CYCLE_PARAM,
  PAGE_PARAM,
  parsePositiveInt,
  parseTab,
  parseTier,
  TAB_PARAM,
  TIER_LABEL,
  TIER_OPTIONS,
  TIER_PARAM,
  type SchoolProfilesTab,
} from '@/components/product-insights/school-profiles/school-profiles-model';
import { useSchoolProfiles } from '@/components/product-insights/school-profiles/use-school-profiles-data';
import { LayoutGrid, LayoutToolbar } from '@/components/layout';
import { SegmentedControl, Select } from '@/components/ui';
import { ApiError } from '@/lib/api';
import { formatDateOnly } from '@/lib/format';
import type { SchoolProfileTierFilter } from '@/lib/types';
import { useAuth } from '@/lib/use-auth';
import { usePageLayout, type LayoutBlockDefault } from '@/lib/use-page-layout';
import { useShowAll } from '@/lib/use-show-all';

/**
 * /product-insights/school-profiles — how much student attention each client
 * school's public profile on SEED's Course Finder is getting (Basic / Premium
 * subscription). Live read through EMERGE's `/product-insights/school-profiles`
 * proxy; nothing stored. Built on the shared Product Insights template
 * (`pi-kit`): header + facts, product nav, toolbar, four tiles, tabs.
 *
 * URL state (every default is an absent key; nothing written on arrival):
 * `cycle` (SEED FAB's current), `tier` (`basic` / `premium`, default all),
 * `page` (1), `tab` (`schools`; absent = Overview). A tier or cycle change
 * resets the page; leaving the Schools tab clears `page`. Reset clears
 * `cycle`, `tier` and `page` and keeps the tab.
 *
 * - The read needs a cycle upstream, so it waits for SEED FAB's cycle list to
 *   resolve the current one. Cycles are SEED's, never EMERGE cycles.
 * - Without `product-insights` read nothing is fetched: the refusal renders
 *   before the data component mounts.
 * - `meta.analyticsDataFrom` is a fact about the data ("Views from …") — view
 *   counts before that date are not reliable — shown in the facts line.
 * - "Show all" is remembered against the account (`useShowAll`), and the read
 *   waits for that preference so it does not fetch page 1 and then everything.
 * - Layout manager (2026-10-20): toolbar, tiles and tabs are fixed; the
 *   Overview tab's panels are movable blocks of
 *   `product-insights.school-profiles.overview`, saved per person. The Schools
 *   tab is one table, so it has no layout. Never rename a block id.
 */

const OVERVIEW_LAYOUT_DEFAULTS: readonly LayoutBlockDefault[] = [
  { id: 'engagement', label: 'Interaction mix, student countries and sources' },
  { id: 'views-by-country', label: 'Profile views by country', width: 'half' },
  { id: 'views-by-channel', label: 'Profile views by channel', width: 'half' },
];

function errorMessage(error: unknown, fallback: string): string {
  if (error instanceof ApiError) return error.message || fallback;
  return fallback;
}

export function ProductInsightsSchoolProfilesPage() {
  const { ability } = useAuth();
  if (!ability.canRead('product-insights')) {
    return <PiDeniedPage product="school-profiles" title="School Profiles" />;
  }
  return <SchoolProfilesContent />;
}

function SchoolProfilesContent() {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlCycleId = parsePositiveInt(searchParams.get(CYCLE_PARAM));
  const tier = parseTier(searchParams.get(TIER_PARAM));
  const page = parsePositiveInt(searchParams.get(PAGE_PARAM)) ?? 1;
  const tab = parseTab(searchParams.get(TAB_PARAM));
  const overviewLayout = usePageLayout(
    'product-insights.school-profiles.overview',
    OVERVIEW_LAYOUT_DEFAULTS,
  );

  const filtersResult = useFabFilters(urlCycleId);
  const { filters } = filtersResult;
  const reloadFilters = filtersResult.reload;

  const selectedCycleId = urlCycleId ?? filters?.resolvedCycleId ?? null;
  const selectedCycle = useMemo(
    () => filters?.cycles.find((c) => c.id === selectedCycleId) ?? null,
    [filters, selectedCycleId],
  );

  const [showAll, setShowAll, showAllLoaded] = useShowAll('product-insights-school-profiles');
  const [reloadToken, setReloadToken] = useState(0);
  const summary = useSchoolProfiles(selectedCycleId, tier, page, showAll, showAllLoaded, reloadToken);

  const refresh = useCallback(() => setReloadToken((n) => n + 1), []);
  const retry = useCallback(() => {
    reloadFilters();
    setReloadToken((n) => n + 1);
  }, [reloadFilters]);

  const update = useCallback(
    (mutate: (params: URLSearchParams) => void) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          mutate(params);
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const onCycleChange = useCallback(
    (event: ChangeEvent<HTMLSelectElement>) => {
      const id = Number.parseInt(event.target.value, 10);
      if (!Number.isSafeInteger(id)) return;
      update((params) => {
        if (filters && id === filters.resolvedCycleId) params.delete(CYCLE_PARAM);
        else params.set(CYCLE_PARAM, String(id));
        params.delete(PAGE_PARAM);
      });
    },
    [filters, update],
  );

  const onTierChange = useCallback(
    (next: SchoolProfileTierFilter) => {
      update((params) => {
        if (next === 'all') params.delete(TIER_PARAM);
        else params.set(TIER_PARAM, next);
        params.delete(PAGE_PARAM);
      });
    },
    [update],
  );

  const onPage = useCallback(
    (next: number) => {
      update((params) => {
        if (next <= 1) params.delete(PAGE_PARAM);
        else params.set(PAGE_PARAM, String(next));
      });
    },
    [update],
  );

  const onTab = useCallback(
    (next: SchoolProfilesTab) => {
      update((params) => {
        if (next === 'overview') {
          params.delete(TAB_PARAM);
          params.delete(PAGE_PARAM);
        } else {
          params.set(TAB_PARAM, next);
        }
      });
    },
    [update],
  );

  const clearCycle = useCallback(
    () =>
      update((params) => {
        params.delete(CYCLE_PARAM);
        params.delete(PAGE_PARAM);
      }),
    [update],
  );

  const clearTier = useCallback(() => onTierChange('all'), [onTierChange]);

  const resetFilters = useCallback(
    () =>
      update((params) => {
        params.delete(CYCLE_PARAM);
        params.delete(TIER_PARAM);
        params.delete(PAGE_PARAM);
      }),
    [update],
  );

  const onToggleShowAll = useCallback(
    (next: boolean) => {
      setShowAll(next);
      onPage(1);
    },
    [setShowAll, onPage],
  );

  const view = summary.view;
  const meta = view?.response.meta ?? null;
  // The data fact is the selected cycle's only once the response says so.
  const viewsFrom =
    meta !== null && meta.cycleId === selectedCycleId ? meta.analyticsDataFrom : null;
  const fetchedAt = meta?.fetchedAt ?? meta?.generatedAt ?? null;
  const pageError = filtersResult.error ?? summary.error;
  const hasError = pageError !== null && pageError !== undefined;
  const dataLoading = summary.loading || (selectedCycleId === null && filters === null);

  // A cycle chip only when the URL names a cycle other than SEED FAB's current one.
  const cycleChipActive =
    urlCycleId !== null && (filters === null || urlCycleId !== filters.resolvedCycleId);
  const chips = [
    ...(cycleChipActive
      ? [
          {
            key: 'cycle',
            label: <>Cycle: {selectedCycle?.name ?? '—'}</>,
            onRemove: clearCycle,
          },
        ]
      : []),
    ...(tier !== 'all'
      ? [{ key: 'tier', label: <>Tier: {TIER_LABEL[tier]}</>, onRemove: clearTier }]
      : []),
  ];

  const facts =
    selectedCycle !== null || viewsFrom !== null ? (
      <>
        {selectedCycle !== null && (
          <>
            {formatDateOnly(selectedCycle.startDate)} – {formatDateOnly(selectedCycle.endDate)}
          </>
        )}
        {selectedCycle !== null && viewsFrom !== null && ' · '}
        {viewsFrom !== null && <>Views from {formatDateOnly(viewsFrom)}</>}
      </>
    ) : undefined;

  const schoolCount = view?.total ?? null;
  const tabs = useMemo<readonly { key: SchoolProfilesTab; label: string; count?: number | null }[]>(
    () => [
      { key: 'overview', label: 'Overview' },
      { key: 'schools', label: 'Schools', count: schoolCount },
    ],
    [schoolCount],
  );

  return (
    <PiPageLayout
      product="school-profiles"
      title="School Profiles"
      facts={facts}
      actions={
        <>
          <UpdatedFact fetchedAt={fetchedAt} refreshing={summary.refreshing} onRefresh={refresh} />
          {tab === 'overview' && <LayoutToolbar layout={overviewLayout} />}
        </>
      }
    >
      <PiToolbar
        chips={
          chips.length > 0 ? <PiFilterChips chips={chips} onReset={resetFilters} /> : undefined
        }
      >
        <div className="w-48 max-sm:w-full">
          <Select
            aria-label="Cycle"
            value={selectedCycleId !== null ? String(selectedCycleId) : ''}
            onChange={onCycleChange}
            disabled={!filters}
          >
            {!filters && <option value="">Cycle</option>}
            {filters?.cycles.map((c) => (
              <option key={c.id} value={String(c.id)}>
                {c.isCurrent ? `${c.name} (current)` : c.name}
              </option>
            ))}
          </Select>
        </div>
        <div className="max-sm:w-full">
          <SegmentedControl label="Tier" value={tier} options={TIER_OPTIONS} onChange={onTierChange} />
        </div>
      </PiToolbar>

      {hasError && (
        <PiError
          message={errorMessage(pageError, 'School Profiles could not be reached.')}
          onRetry={retry}
        />
      )}

      {(view !== null || !hasError) && (
        <>
          <SchoolProfilesStats data={view?.response ?? null} loading={dataLoading} />
          <PiTabs tabs={tabs} value={tab} onChange={onTab} ariaLabel="School Profiles views" />
          {tab === 'overview' ? (
            <LayoutGrid
              layout={overviewLayout}
              gap="sm"
              render={{
                engagement: () => (
                  <SchoolProfilesEngagement data={view?.response ?? null} loading={dataLoading} />
                ),
                'views-by-country': () => (
                  <SchoolProfilesViewsByCountry data={view?.response ?? null} loading={dataLoading} />
                ),
                'views-by-channel': () => (
                  <SchoolProfilesViewsByChannel data={view?.response ?? null} loading={dataLoading} />
                ),
              }}
            />
          ) : (
            <SchoolProfilesTable
              view={view}
              loading={dataLoading}
              refreshing={summary.refreshing}
              showAll={showAll}
              onPage={onPage}
              onToggleShowAll={onToggleShowAll}
            />
          )}
        </>
      )}
    </PiPageLayout>
  );
}

export default ProductInsightsSchoolProfilesPage;
